import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersApi, rolesApi } from '../lib/api';
import { formatDate, ROLE_LABELS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, MagnifyingGlass, UserCircle, Pencil, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AgentStoreInfoFields } from '../components/users/AgentStoreInfoFields';

const EMPTY_STORE_INFO = {
    store_name: '', store_code: '', address: '', city: '', province: '',
    cap: '', phone: '', mobile_phone: '', email: '', job_title: '',
};

export default function UsersPage() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        role: 'viewer',
        store_info: { ...EMPTY_STORE_INFO },
    });

    const loadData = useCallback(async () => {
        try {
            const [usersRes, rolesRes] = await Promise.all([
                usersApi.getAll(),
                rolesApi.getAll()
            ]);
            setUsers(usersRes.data);
            setRoles(rolesRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const resetForm = () => {
        setFormData({
            email: '',
            password: '',
            name: '',
            role: 'viewer',
            store_info: { ...EMPTY_STORE_INFO },
        });
        setEditingUser(null);
    };

    const isAgentRole = formData.role === 'store_agent' || formData.role === 'responsabile_store';

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Includi store_info SOLO se ruolo store_agent o ha qualche valore
            const storeInfoPayload = isAgentRole
                ? formData.store_info
                : (Object.values(formData.store_info || {}).some((v) => v && v.toString().trim() !== '')
                    ? formData.store_info : null);

            if (editingUser) {
                await usersApi.update(editingUser.id, {
                    name: formData.name,
                    role: formData.role,
                    password: formData.password || undefined,
                    store_info: storeInfoPayload,
                });
                toast.success('Utente aggiornato');
            } else {
                await usersApi.create({
                    email: formData.email,
                    password: formData.password,
                    name: formData.name,
                    role: formData.role,
                    store_info: storeInfoPayload,
                });
                toast.success('Utente creato');
            }
            setDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            email: user.email,
            password: '',
            name: user.name,
            role: user.role,
            store_info: { ...EMPTY_STORE_INFO, ...(user.store_info || {}) },
        });
        setDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        try {
            await usersApi.delete(userToDelete.id);
            toast.success('Utente eliminato');
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            loadData();
        } catch (error) {
            console.error('Error:', error);
            toast.error('Errore nell\'eliminazione');
        }
    };

    const filteredUsers = users.filter(u => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            u.name?.toLowerCase().includes(searchLower) ||
            u.email?.toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="users-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Cerca utenti..."
                        data-testid="users-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button data-testid="create-user-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Nuovo Utente
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Nome completo *</Label>
                                    <Input
                                        data-testid="user-name-input"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                        placeholder="Es: Gian Luigi Accattato"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Email di accesso *</Label>
                                    <Input
                                        type="email"
                                        data-testid="user-email-input"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        required
                                        disabled={!!editingUser}
                                        placeholder="gianluigi.accattato@mainsolution.eu"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>{editingUser ? 'Nuova Password (vuoto = invariata)' : 'Password *'}</Label>
                                    <Input
                                        type="password"
                                        data-testid="user-password-input"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        required={!editingUser}
                                        minLength={6}
                                        placeholder="Min 6 caratteri"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Ruolo *</Label>
                                    <Select 
                                        value={formData.role} 
                                        onValueChange={(v) => setFormData({...formData, role: v})}
                                    >
                                        <SelectTrigger data-testid="user-role-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roles.map(role => (
                                                <SelectItem key={role.id} value={role.id}>
                                                    {role.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Dati commerciali — visibili sempre, evidenziati per store_agent */}
                            <div className={`rounded-lg border-2 p-4 ${
                                isAgentRole
                                    ? 'border-[#00BCE7]/30 bg-cyan-50/30'
                                    : 'border-slate-200 bg-slate-50/50'
                            }`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <h4 className="text-sm font-bold text-[#0B2A4A]">
                                            Dati commerciali {isAgentRole && <span className="text-[#00BCE7]">— consigliati per agenti</span>}
                                        </h4>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            Telefono, email, sede store — verranno mostrati nei PDF dei preventivi.
                                        </p>
                                    </div>
                                </div>
                                <AgentStoreInfoFields
                                    value={formData.store_info}
                                    onChange={(v) => setFormData({ ...formData, store_info: v })}
                                />
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => {
                                    setDialogOpen(false);
                                    resetForm();
                                }}>
                                    Annulla
                                </Button>
                                <Button type="submit" data-testid="submit-user-button">
                                    {editingUser ? 'Salva Modifiche' : 'Crea Utente'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Users Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-header">
                                <TableHead>Utente</TableHead>
                                <TableHead>Ruolo</TableHead>
                                <TableHead>Data Creazione</TableHead>
                                <TableHead className="w-24"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-12">
                                        <UserCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                                        <p className="text-muted-foreground">Nessun utente trovato</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredUsers.map((user) => (
                                    <TableRow 
                                        key={user.id} 
                                        className="table-row"
                                        data-testid={`user-row-${user.id}`}
                                    >
                                        <TableCell className="table-cell">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                                    <UserCircle weight="fill" className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="font-medium">{user.name}</p>
                                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <Badge variant="outline">
                                                {ROLE_LABELS[user.role] || user.role}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="table-cell text-muted-foreground">
                                            {formatDate(user.created_at)}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <div className="flex items-center gap-1">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    onClick={() => handleEdit(user)}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                {user.id !== currentUser?.id && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        className="text-destructive hover:text-destructive"
                                                        onClick={() => {
                                                            setUserToDelete(user);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                    >
                                                        <Trash className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
                        <AlertDialogDescription>
                            Sei sicuro di voler eliminare l&apos;utente <strong>{userToDelete?.name}</strong>?
                            Questa azione non può essere annullata.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
